import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { buildApiUrl, buildPublicFileUrl } from "../config/api";
import "./AdminCollections.css";

function imageUrl(path) {
  if (!path) return "";
  if (/^https?:/i.test(path)) return path;
  return path.startsWith("/") || path.startsWith("uploads/")
    ? buildPublicFileUrl(path)
    : path;
}

export default function AdminCollections() {
  const [products, setProducts] = useState([]);
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    fetch(buildApiUrl("/products/admin/all"), { credentials: "include", headers: { Accept: "application/json" } })
      .then(async (response) => {
        const data = await response.json();
        if (!response.ok || !data.success) throw new Error(data.message || "Could not load collections.");
        return data.products || [];
      })
      .then(setProducts)
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, []);

  const collections = useMemo(() => {
    const groups = new Map();
    products.filter((product) => product.status !== "deleted").forEach((product) => {
      const name = String(product.collection || product.collectionName || product.category || "Uncategorized").trim();
      if (!groups.has(name)) groups.set(name, []);
      groups.get(name).push(product);
    });
    const needle = query.trim().toLowerCase();
    return [...groups.entries()]
      .map(([name, items]) => ({ name, items, published: items.filter((item) => item.status === "published").length }))
      .filter((group) => !needle || group.name.toLowerCase().includes(needle) || group.items.some((item) => String(item.title || "").toLowerCase().includes(needle)))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [products, query]);

  return (
    <div className="admin-collections-page">
      <aside className="admin-collections-sidebar">
        <div><strong>MayaChitro</strong><span>Admin Panel</span></div>
        <nav><Link to="/admin">Dashboard</Link><Link to="/admin/products">Photos / Products</Link><Link className="active" to="/admin/collections">Collections</Link><Link to="/admin/image-optimizer">Image Optimizer</Link><Link to="/admin/orders">Orders & Sales</Link><Link to="/admin/customers">Customers</Link></nav>
      </aside>
      <main>
        <header><div><p>Catalog organization</p><h1>Collections</h1><span>Review how photographs are grouped and open any product to change its collection.</span></div><Link to="/admin/products">Add or edit products</Link></header>
        <section className="collection-summary"><div><span>Collections</span><strong>{collections.length}</strong></div><div><span>Active photos</span><strong>{products.filter((item) => item.status !== "deleted").length}</strong></div><div><span>Published</span><strong>{products.filter((item) => item.status === "published").length}</strong></div></section>
        <div className="collection-toolbar"><input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Search collections or photographs" />{query && <button type="button" onClick={() => setQuery("")}>Clear</button>}</div>
        {error && <div className="collection-error">{error}</div>}
        {loading ? <div className="collection-empty">Loading collections...</div> : collections.length ? <section className="collection-grid">{collections.map((collection) => <article key={collection.name}><div className="collection-card-head"><div><h2>{collection.name}</h2><span>{collection.items.length} photos · {collection.published} published</span></div></div><div className="collection-photo-strip">{collection.items.slice(0, 5).map((product) => { const src = imageUrl(product.image || product.coverImage || product.thumbnailImage); return <Link title={product.title} to={`/admin/products?edit=${product.id || product._id}`} key={product.id || product._id}>{src ? <img src={src} alt={product.title} /> : <span>No image</span>}</Link>; })}</div><div className="collection-products">{collection.items.slice(0, 4).map((product) => <Link to={`/admin/products?edit=${product.id || product._id}`} key={product.id || product._id}><span>{product.productId}</span><strong>{product.title}</strong><em className={product.status}>{product.status === "published" ? "Published" : "Unpublished"}</em></Link>)}</div>{collection.items.length > 4 && <small>+{collection.items.length - 4} more photographs</small>}</article>)}</section> : <div className="collection-empty">No matching collections.</div>}
      </main>
    </div>
  );
}
